import React, { useState, useMemo, useRef } from 'react';
import { Container, Card, Form, Button, Stack, Table, Spinner, OverlayTrigger, Popover } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../axiosConfig';
import Bottom from '../../Layouts/Bottom/Bottom';
import DateRangePicker from '../../../Components/DateRangePicker/DateRangePicker';

const SearchDefectPerMotifDyeing = () => {
  document.title = "Search Defect Per Motif Dyeing";  
  
  const [data, setData] = useState({ kain_dalam: [], kain_luar: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({});
  const [selectedRange, setSelectedRange] = useState();

  // Ref untuk area tabel yang akan dicetak
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Rekap Panjang Defect By Motif dan Grade Dyeing",
    pageStyle: `
      @media print {
        body {
          background: white !important;
          color: black !important;
          font-family: Arial, sans-serif;
          font-size: 10px !important;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          border: 1px solid black !important;
          padding: 4px !important;
          background-color: transparent !important;
          color: black !important;
          font-size: 10px !important;
        }
        /* Memaksa semua teks atau link yang tadinya berwarna biru menjadi hitam pekat saat diprint */
        .text-primary, div.text-primary, a {
          color: black !important;
        }
      }
    `
  });

  const fetchDataIndex = async () => {
    setIsLoading(true);
    try {
      if (!params.start_date || !params.end_date) {
        alert('Tanggal harus diisi');
        return;
      } else if (new Date(params.end_date) < new Date(params.start_date)) {
        alert('Tanggal akhir tidak boleh lebih kecil dari tanggal awal');
        return;
      } else if ((new Date(params.end_date) - new Date(params.start_date)) / (1000 * 60 * 60 * 24) > 30) {
        alert('Jarak tanggal awal ke akhir tidak boleh lebih dari 30 hari');
        return;
      } else {
        const response = await axiosInstance.get(`defect-item/get-defect-meter-tgl-kirim`, { params });
        setData(response.data.data || { kain_dalam: [], kain_luar: [] });
        console.log('Data fetched successfully:', response.data.data);
      }
    } catch (error) {
      console.log(error.response);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRange = (range) => {
    setSelectedRange(range);
    if (range?.from && range?.to) {
      setParams((prevParams) => ({
        ...prevParams,
        start_date: range.from,
        end_date: range.to,
      }));
    } else {
      setParams((prevParams) => ({
        ...prevParams,
        start_date: null,
        end_date: null,
      }));
    }
  };

  const combinedData = useMemo(() => {
    const map = new Map();

    (data.kain_dalam || []).forEach((item) => {
      map.set(item.no_urut, {
        no_urut: item.no_urut,
        nama_defect: item.nama_defect,
        kain_dalam: item,
        kain_luar: null,
      });
    });

    (data.kain_luar || []).forEach((item) => {
      if (map.has(item.no_urut)) {
        map.get(item.no_urut).kain_luar = item;
      } else {
        map.set(item.no_urut, {
          no_urut: item.no_urut,
          nama_defect: item.nama_defect,
          kain_dalam: null,
          kain_luar: item,
        });
      }
    });

    const result = Array.from(map.values());

    return result.sort((a, b) => {
      const totalKainDalamA = (a.kain_dalam?.total_grade_2 || 0) + (a.kain_dalam?.total_grade_3 || 0);
      const totalKainDalamB = (b.kain_dalam?.total_grade_2 || 0) + (b.kain_dalam?.total_grade_3 || 0);
      
      return totalKainDalamB - totalKainDalamA;
    });
  }, [data]);

  const totals = useMemo(() => {
    return combinedData.reduce(
      (acc, item) => {
        const kdG2 = item.kain_dalam?.total_grade_2 || 0;
        const kdG3 = item.kain_dalam?.total_grade_3 || 0;
        const klG2 = item.kain_luar?.total_grade_2 || 0;
        const klG3 = item.kain_luar?.total_grade_3 || 0;

        acc.kdG2 += kdG2;
        acc.kdG3 += kdG3;
        acc.kdTotal += kdG2 + kdG3;

        acc.klG2 += klG2;
        acc.klG3 += klG3;
        acc.klTotal += klG2 + klG3;

        acc.grandTotal += kdG2 + kdG3 + klG2 + klG3;

        return acc;
      },
      { kdG2: 0, kdG3: 0, kdTotal: 0, klG2: 0, klG3: 0, klTotal: 0, grandTotal: 0 }
    );
  }, [combinedData]);

  // Fungsi Helper untuk Export Excel (Hanya Mentahan Angka / Data Murni)
  const handleExportExcel = () => {
    if (combinedData.length === 0) {
      alert('Tidak ada data untuk diexport!');
      return;
    }

    // Mapping data menjadi array objek murni berupa angka/string bersih tanpa format HTML
    const excelData = combinedData.map((item, index) => {
      const kdG2Total = item.kain_dalam?.total_grade_2 || 0;
      const kdG3Total = item.kain_dalam?.total_grade_3 || 0;
      const totalKainDalam = kdG2Total + kdG3Total;

      const klG2Total = item.kain_luar?.total_grade_2 || 0;
      const klG3Total = item.kain_luar?.total_grade_3 || 0;
      const totalKainLuar = klG2Total + klG3Total;

      const grandTotalRow = totalKainDalam + totalKainLuar;

      return {
        "No": index + 1,
        "Kode Defect": item.no_urut,
        "Nama Defect": item.nama_defect,
        "Kain Dalam - Grade B": kdG2Total > 0 ? Number(kdG2Total.toFixed(2)) : 0,
        "Kain Dalam - Grade C": kdG3Total > 0 ? Number(kdG3Total.toFixed(2)) : 0,
        "Kain Dalam - Total": totalKainDalam > 0 ? Number(totalKainDalam.toFixed(2)) : 0,
        "Kain Luar - Grade B": klG2Total > 0 ? Number(klG2Total.toFixed(2)) : 0,
        "Kain Luar - Grade C": klG3Total > 0 ? Number(klG3Total.toFixed(2)) : 0,
        "Kain Luar - Total": totalKainLuar > 0 ? Number(totalKainLuar.toFixed(2)) : 0,
        "Grand Total": grandTotalRow > 0 ? Number(grandTotalRow.toFixed(2)) : 0,
      };
    });

    // Tambahkan baris Grand Total di akhir data excel
    excelData.push({
      "No": "",
      "Kode Defect": "",
      "Nama Defect": "Grand Total Keseluruhan",
      "Kain Dalam - Grade B": Number(totals.kdG2.toFixed(2)),
      "Kain Dalam - Grade C": Number(totals.kdG3.toFixed(2)),
      "Kain Dalam - Total": Number(totals.kdTotal.toFixed(2)),
      "Kain Luar - Grade B": Number(totals.klG2.toFixed(2)),
      "Kain Luar - Grade C": Number(totals.klG3.toFixed(2)),
      "Kain Luar - Total": Number(totals.klTotal.toFixed(2)),
      "Grand Total": Number(totals.grandTotal.toFixed(2)),
    });

    // Buat Worksheet & Workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rekap Defect Dyeing");

    // Simpan file excel
    XLSX.writeFile(workbook, "Rekap_Defect_Per_Motif_Dyeing.xlsx");
  };

  const renderGradeCell = (gradeList, keyPrefix) => {
    if (!gradeList || gradeList.length === 0) return '-';

    return gradeList
      .slice()
      .sort((a, b) => Number(b.panjang) - Number(a.panjang))
      .map((grade, i) => {
        const popover = (
          <Popover id={`popover-${keyPrefix}-${i}`}>
            <Popover.Header as="h3" style={{ fontSize: '12px' }}>Daftar No. Kartu</Popover.Header>
            <Popover.Body style={{ maxHeight: '150px', overflowY: 'auto', padding: '10px' }}>
              <ul style={{ paddingLeft: '15px', margin: 0, fontSize: '11px' }}>
                {Array.isArray(grade.no_kartu) ? (
                  grade.no_kartu.map((no, idx) => <li key={idx}>{no}</li>)
                ) : (
                  <li>{grade.no_kartu || '-'}</li>
                )}
              </ul>
            </Popover.Body>
          </Popover>
        );

        return (
          <OverlayTrigger
            key={i}
            trigger="click"
            rootClose
            placement="top"
            overlay={popover}
          >
            <div style={{ textAlign: 'left', cursor: 'pointer' }} className="text-primary">
              {`${grade.nama_kain} = ${Number(grade.panjang).toFixed(2)}`}
            </div>
          </OverlayTrigger>
        );
      });
  };

  return (
    <>
      <div className="vh-100" style={{ position: "relative", height: "30rem" }}>
        <div
          style={{ height: "70%", justifyContent: "center" }}
          className="bg-burgundy-gradient bg-pattern-container text-white p-4 curved-container"
        ></div>

        <div className="p-4" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
          <Container fluid className="py-5 container-padding-bottom">
            <Stack direction="vertical" gap={3} style={{ marginBottom: "200px" }}>
              <div className='py-4'>
                <h1 className='text-white'>Rekap Panjang Defect By Motif dan Grade Dyeing</h1>
                
                <div className="mt-4 d-flex justify-content-between align-items-end w-100">
                  <Form className="d-flex gap-2 w-50 align-items-end">
                    <Form.Group controlId="tahun" className='flex-grow-1'>
                      <Form.Label className="text-white">Tanggal</Form.Label>
                      <Stack direction="horizontal" gap={3}>
                        <DateRangePicker value={selectedRange} onChange={handleChangeRange} />
                        <Button variant="burgundy" className='mt-auto' onClick={fetchDataIndex} disabled={isLoading}>
                          {isLoading ? <><Spinner animation="border" size="sm" /> Tunggu sebentar</> : 'Cari'}
                        </Button>
                      </Stack>
                    </Form.Group>
                  </Form>

                  <div className="d-flex gap-2">
                    <Button variant="success" className="d-flex align-items-center gap-2" onClick={handleExportExcel}>
                      Export Excel
                    </Button>
                    <Button variant="light" className="d-flex align-items-center gap-2" onClick={handlePrint}>
                      Print / Cetak
                    </Button>
                  </div>
                </div>
              </div>

              <Card>
                <Card.Body>
                  {/* Hanya area di dalam ref ini yang akan dicetak */}
                  <div ref={componentRef}>
                    <h3 className='text-center'>Laporan Rekap Panjang Defect By Motif dan Grade Dyeing</h3>
                    <p className="text-center m-0">
                      Tanggal Dari : {selectedRange?.from?.toLocaleDateString('id-ID') || ''} s/d {selectedRange?.to?.toLocaleDateString('id-ID') || ''}
                    </p>
                    <Table className="text-center mt-3" responsive bordered>
                      <thead>
                        <tr>
                          <th rowSpan="2" className="align-middle">No</th>
                          <th rowSpan="2" className="align-middle">Kode Defect</th>
                          <th rowSpan="2" className="align-middle">Nama Defect</th>
                          
                          <th colSpan="3" className="align-middle">Kain Dalam</th>
                          <th colSpan="3" className="align-middle">Kain Luar</th>
                          
                          <th rowSpan="2" className="align-middle">Grand Total</th>
                        </tr>
                        <tr>
                          <th>Grade B</th>
                          <th>Grade C</th>
                          <th>Total</th>
                          
                          <th>Grade B</th>
                          <th>Grade C</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {combinedData.length > 0 ? (
                          <>
                            {combinedData.map((item, index) => {
                              const kdG2Total = item.kain_dalam?.total_grade_2 || 0;
                              const kdG3Total = item.kain_dalam?.total_grade_3 || 0;
                              const totalKainDalam = kdG2Total + kdG3Total;

                              const klG2Total = item.kain_luar?.total_grade_2 || 0;
                              const klG3Total = item.kain_luar?.total_grade_3 || 0;
                              const totalKainLuar = klG2Total + klG3Total;

                              const grandTotalRow = totalKainDalam + totalKainLuar;

                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.no_urut}</td>
                                  <td style={{ textAlign: 'left' }}>{item.nama_defect}</td>
                                  
                                  <td style={{ whiteSpace: 'pre-wrap' }} className="text-left">
                                    {renderGradeCell(item.kain_dalam?.grade_2, `kd-g2-${index}`)}
                                  </td>
                                  <td style={{ whiteSpace: 'pre-wrap' }} className="text-left">
                                    {renderGradeCell(item.kain_dalam?.grade_3, `kd-g3-${index}`)}
                                  </td>
                                  <td>
                                    {totalKainDalam > 0 ? totalKainDalam.toFixed(2) : '-'}
                                  </td>

                                  <td style={{ whiteSpace: 'pre-wrap' }} className="text-left">
                                    {renderGradeCell(item.kain_luar?.grade_2, `kl-g2-${index}`)}
                                  </td>
                                  <td style={{ whiteSpace: 'pre-wrap' }} className="text-left">
                                    {renderGradeCell(item.kain_luar?.grade_3, `kl-g3-${index}`)}
                                  </td>
                                  <td>
                                    {totalKainLuar > 0 ? totalKainLuar.toFixed(2) : '-'}
                                  </td>

                                  <td>
                                    {grandTotalRow.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}

                            {/* Baris Grand Total Keseluruhan diletakkan di akhir tbody */}
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'right' }}>
                                <strong>Grand Total Keseluruhan:</strong>
                              </td>
                              <td><strong>{totals.kdG2.toFixed(2)}</strong></td>
                              <td><strong>{totals.kdG3.toFixed(2)}</strong></td>
                              <td><strong>{totals.kdTotal.toFixed(2)}</strong></td>
                              <td><strong>{totals.klG2.toFixed(2)}</strong></td>
                              <td><strong>{totals.klG3.toFixed(2)}</strong></td>
                              <td><strong>{totals.klTotal.toFixed(2)}</strong></td>
                              <td><strong>{totals.grandTotal.toFixed(2)}</strong></td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-muted">Data tidak ditemukan</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>

            </Stack>
          </Container>
        </div>
      </div>
      <Bottom />
    </>
  );
};

export default SearchDefectPerMotifDyeing;